import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import IncidentSection from "../../../components/patientChart/IncidentSection";
import SignatureSection from "../../../components/patientChart/SignatureSection";
import { generateIncidentReportPdf } from "../../../services/documentGenerationService";
import { buildIncidentReportTemplateData } from "../../../utils/patient/incidentReportMapper";
import { updateEncounter } from "../../../services/patientService";

const cardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 14,
  background: "#fff",
  display: "grid",
  gap: 12,
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  color: "#0f172a",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 14,
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 100,
  resize: "vertical",
};

const toggleWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const toggleButtonStyle = (selected) => ({
  border: selected ? "1px solid #0f172a" : "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: selected ? "#0f172a" : "#fff",
  color: selected ? "#fff" : "#0f172a",
  cursor: "pointer",
  fontWeight: 600,
});

const primaryButtonStyle = {
  border: "none",
  borderRadius: 10,
  minHeight: 44,
  padding: "0 16px",
  background: "#0f766e",
  color: "#fff",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 800,
};

const secondaryButtonStyle = {
  ...primaryButtonStyle,
  background: "#fff",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 20,
};

const overlayCardStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
  minWidth: 280,
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  display: "grid",
  gap: 14,
  justifyItems: "center",
  textAlign: "center",
};

const spinnerStyle = {
  width: 42,
  height: 42,
  border: "4px solid #dbeafe",
  borderTop: "4px solid #0f766e",
  borderRadius: "50%",
  animation: "incident-spin 1s linear infinite",
};

function formatDateInput(date = new Date()) {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatTimeInput(date = new Date()) {
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPhoneInput(value = "") {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);

  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
    6,
    10
  )}`;
}

function splitName(fullName = "") {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function YesNoButtons({ value, onChange, disabled = false }) {
  return (
    <div style={toggleWrapStyle}>
      <button
        type="button"
        style={{
          ...toggleButtonStyle(value === true),
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        onClick={() => onChange(true)}
        disabled={disabled}
      >
        Yes
      </button>

      <button
        type="button"
        style={{
          ...toggleButtonStyle(value === false),
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        onClick={() => onChange(false)}
        disabled={disabled}
      >
        No
      </button>
    </div>
  );
}

function buildIncidentDocumentRecord({ patientPayload, encounterForm, result }) {
  const displayName = patientPayload.fullName || "Patient";
  const datePart =
    encounterForm.arrivalDate || new Date().toISOString().slice(0, 10);
  const fileName =
    result?.fileName || `Incident Report - ${displayName} - ${datePart}.pdf`;

  return {
    id: `incident-${Date.now()}`,
    name: fileName,
    fileName,
    url: result?.pdfUrl || "",
    downloadURL: result?.pdfUrl || "",
    uploadedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    type: "application/pdf",
    mimeType: "application/pdf",
    category: "incident-report",
    source: "cloud-function",
    driveFileId: result?.driveFileId || "",
    templateId: result?.templateId || "",
    generatedAt: result?.generatedAt || new Date().toISOString(),
  };
}

export default function IncidentReportPage() {
  const navigate = useNavigate();

  const sessionData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("activeIncidentReport") || "{}");
    } catch {
      return {};
    }
  }, []);

  const call = sessionData?.call || {};

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const [patient, setPatient] = useState({
    firstName: "",
    lastName: "",
    fullName: "",
    phone: "",
    email: "",
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
  });

  const [encounterForm, setEncounterForm] = useState({
    arrivalDate: "",
    arrivalTime: "",
    reportCompletedBy: "",
    chiefComplaint: "",
  });

  const [medicalForm, setMedicalForm] = useState({
    firstAidGiven: false,
    bodyPartInjured: "",
    planTreatmentDisposition: "",
    disposition: "",
  });

  const [incidentForm, setIncidentForm] = useState({
    claimantType: "",
    claimantTypeOther: "",
    vendorName: "",
    description: "",
    descriptionOfIncident: "",
    conditions: {},
    causes: {},
    location: {},
    witnessPresent: false,
    witnessName: "",
    witnessAddress: "",
    witnessPhone: "",
    photosTaken: false,
    photos: [],
    firstAidGiven: false,
    otherInformation: "",
    otherInformationNA: false,
    playerName: "",
    golfCartNumber: "",
    golfCartDriverName: "",
    companyName: "",
  });

  const [signatureForm, setSignatureForm] = useState({
    patientSignature: "",
    providerSignature: "",
    unableToObtain: false,
  });

  const returnPath = useMemo(() => {
    return sessionData?.unitSlug
      ? `/field-access/responder/${sessionData.unitSlug}`
      : "/field-access/responder";
  }, [sessionData?.unitSlug]);

  useEffect(() => {
    let cancelled = false;

    async function loadInitial() {
      const now = new Date();

      const initialName = splitName(call.name || call.patientName || "");
      const nextPatient = {
        firstName: initialName.firstName,
        lastName: initialName.lastName,
        fullName: [initialName.firstName, initialName.lastName]
          .filter(Boolean)
          .join(" "),
        phone: formatPhoneInput(call.phone || call.phoneNumber || ""),
        email: call.email || "",
        streetAddress: call.address || "",
        city: call.city || "",
        state: call.state || "",
        zip: call.zip || "",
      };

      if (sessionData.patientId) {
        try {
          const patientSnap = await getDoc(
            doc(db, "patients", sessionData.patientId)
          );

          if (patientSnap.exists()) {
            const p = patientSnap.data() || {};
            const patientName = splitName(
              p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ")
            );

            nextPatient.firstName = patientName.firstName || nextPatient.firstName;
            nextPatient.lastName = patientName.lastName || nextPatient.lastName;
            nextPatient.fullName = [nextPatient.firstName, nextPatient.lastName]
              .filter(Boolean)
              .join(" ");
            nextPatient.phone = formatPhoneInput(p.phone || nextPatient.phone);
            nextPatient.email = p.email || nextPatient.email;
            nextPatient.streetAddress =
              p.streetAddress ||
              p.addressLine1 ||
              p.address ||
              nextPatient.streetAddress;
            nextPatient.city = p.city || nextPatient.city;
            nextPatient.state = p.state || nextPatient.state;
            nextPatient.zip = p.zip || p.postalCode || nextPatient.zip;
          }
        } catch (err) {
          console.error("Error loading patient for incident report:", err);
        }
      }

      if (cancelled) return;

      setPatient(nextPatient);

      setEncounterForm({
        arrivalDate: formatDateInput(now),
        arrivalTime: formatTimeInput(now),
        reportCompletedBy: "",
        chiefComplaint: call.nature || "",
      });

      setMedicalForm({
        firstAidGiven: false,
        bodyPartInjured: "",
        planTreatmentDisposition: "",
        disposition: "",
      });

      setIncidentForm({
        claimantType: "",
        claimantTypeOther: "",
        vendorName: "",
        description: call.nature || "",
        descriptionOfIncident: call.nature || "",
        conditions: {},
        causes: {},
        location: {
          other: Boolean(call.location),
          otherText: call.location || "",
        },
        witnessPresent: false,
        witnessName: "",
        witnessAddress: "",
        witnessPhone: "",
        photosTaken: false,
        photos: [],
        firstAidGiven: false,
        otherInformation: "",
        otherInformationNA: false,
        playerName: "",
        golfCartNumber: "",
        golfCartDriverName: "",
        companyName: "",
      });

      setLoading(false);
    }

    loadInitial();

    return () => {
      cancelled = true;
    };
  }, [
    call.address,
    call.city,
    call.email,
    call.location,
    call.name,
    call.nature,
    call.patientName,
    call.phone,
    call.phoneNumber,
    call.state,
    call.zip,
    sessionData.patientId,
  ]);

  function setPatientField(field, value) {
    setPatient((prev) => {
      const next = {
        ...prev,
        [field]: field === "phone" ? formatPhoneInput(value) : value,
      };

      if (field === "firstName" || field === "lastName") {
        next.fullName = [
          field === "firstName" ? value : next.firstName,
          field === "lastName" ? value : next.lastName,
        ]
          .filter(Boolean)
          .join(" ");
      }

      if (field === "fullName") {
        const split = splitName(value);
        next.firstName = split.firstName;
        next.lastName = split.lastName;
        next.fullName = value;
      }

      return next;
    });
  }

  function setEncounterField(field, value) {
    setEncounterForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function setMedicalField(field, value) {
    setMedicalForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function setSignatureField(field, value) {
    setSignatureForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleCancel() {
    if (generating) return;
    navigate(returnPath, { replace: true });
  }

  async function handleGenerate() {
    if (generating) return;

    setGenerating(true);
    setError("");
    setSaveMessage("");

    try {
      const patientPayload = {
        ...patient,
        fullName: [patient.firstName, patient.lastName]
          .filter(Boolean)
          .join(" ")
          .trim(),
      };

      const encounter = {
        ...encounterForm,
        notes: "",
        providerName: encounterForm.reportCompletedBy || "",
        medical: medicalForm,
        incident: incidentForm,
        signature: signatureForm,
      };

      const normalizedIncidentForm = {
        ...incidentForm,
        descriptionOfIncident:
          incidentForm.descriptionOfIncident ||
          incidentForm.description ||
          encounterForm.chiefComplaint ||
          "",
        otherLocationDescription: incidentForm?.location?.otherText || "",
        otherConditionText: incidentForm?.conditions?.otherText || "",
      };

      const templateData = buildIncidentReportTemplateData({
        patient: patientPayload,
        encounter,
        incidentForm: normalizedIncidentForm,
        medicalForm,
        signatureForm,
      });

      const result = await generateIncidentReportPdf({
        patientId: sessionData.patientId || "",
        encounterId: sessionData.encounterId || "",
        templateData,
        patient: patientPayload,
        encounter,
        photos: (incidentForm.photos || []).map((photo) => {
          const raw =
            photo?.dataUrl ||
            photo?.base64 ||
            photo?.url ||
            photo?.preview ||
            "";

          const base64 = String(raw).includes(",")
            ? String(raw).split(",")[1]
            : String(raw);

          return {
            base64: base64 || "",
            name: photo?.name || "",
          };
        }),
      });

      if (!result?.pdfUrl || !result?.driveFileId) {
        throw new Error("Final Drive PDF was not returned.");
      }

      const incidentRecord = {
        createdAt: serverTimestamp(),
        generatedAt: result.generatedAt || new Date().toISOString(),
        patient: patientPayload,
        encounter,
        incidentForm: {
          ...incidentForm,
          photos: (incidentForm.photos || []).map((photo) => ({
            name: photo.name || "",
          })),
        },
        medicalForm,
        signatureForm: {
          patientSignature: Boolean(signatureForm.patientSignature),
          providerSignature: Boolean(signatureForm.providerSignature),
          unableToObtain: Boolean(signatureForm.unableToObtain),
        },
        fileName:
          result.fileName ||
          `Incident Report - ${patientPayload.fullName || "Unknown Patient"}.pdf`,
        pdfUrl: result.pdfUrl,
        driveFileId: result.driveFileId,
        templateId: result.templateId || "",
        photoCount: Array.isArray(incidentForm.photos)
          ? incidentForm.photos.length
          : 0,
        photoNames: (incidentForm.photos || []).map((photo) => photo.name || ""),
        call: sessionData.call || {},
        unit: sessionData.unitLabel || "",
        unitId: sessionData.unitId || "",
        unitSlug: sessionData.unitSlug || "",
        encounterId: sessionData.encounterId || "",
        patientId: sessionData.patientId || "",
      };

      await addDoc(collection(db, "incident_reports"), incidentRecord);

      if (sessionData.patientId && sessionData.encounterId) {
        let existingDocuments = [];

        try {
          const encounterSnap = await getDoc(
            doc(
              db,
              "patients",
              sessionData.patientId,
              "encounters",
              sessionData.encounterId
            )
          );

          if (encounterSnap.exists()) {
            existingDocuments = Array.isArray(encounterSnap.data()?.documents)
              ? encounterSnap.data().documents
              : [];
          }
        } catch (err) {
          console.error("Error loading encounter documents:", err);
        }

        const generatedDoc = buildIncidentDocumentRecord({
          patientPayload,
          encounterForm,
          result,
        });

        await updateEncounter(sessionData.patientId, sessionData.encounterId, {
          documents: [...existingDocuments, generatedDoc],
        });
      }

      sessionStorage.removeItem("activeIncidentReport");
      setSaveMessage("Incident Report generated successfully.");

      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            { type: "incident-report:submitted", at: Date.now() },
            window.location.origin
          );
        }
      } catch {
        // ignore
      }

      try {
        window.close();
      } catch {
        // ignore
      }

      setTimeout(() => {
        if (!window.closed) {
          navigate(returnPath, { replace: true });
        }
      }, 300);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Error generating report.");
      setGenerating(false);
      return;
    }

    setGenerating(false);
  }

  if (loading) {
    return <div style={{ padding: 16 }}>Loading...</div>;
  }

  return (
    <>
      <style>
        {`
          @keyframes incident-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      {generating && (
        <div style={overlayStyle}>
          <div style={overlayCardStyle}>
            <div style={spinnerStyle} />
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
              Generating Incident Report
            </div>
            <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.5 }}>
              Please wait while the report and attachments are being compiled and
              saved.
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          padding: 16,
          display: "grid",
          gap: 16,
          maxWidth: 1100,
          margin: "0 auto",
          opacity: generating ? 0.7 : 1,
          pointerEvents: generating ? "none" : "auto",
          transition: "opacity 0.2s ease",
        }}
      >
        {(error || saveMessage) && (
          <div
            style={{
              ...cardStyle,
              color: error ? "#b91c1c" : "#166534",
              borderColor: error ? "#fecaca" : "#bbf7d0",
              background: error ? "#fef2f2" : "#f0fdf4",
            }}
          >
            {error || saveMessage}
          </div>
        )}

        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Patient Information</h2>

          <div style={gridStyle}>
            <label style={labelStyle}>
              First Name
              <input
                style={inputStyle}
                value={patient.firstName}
                onChange={(e) => setPatientField("firstName", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Last Name
              <input
                style={inputStyle}
                value={patient.lastName}
                onChange={(e) => setPatientField("lastName", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Phone
              <input
                style={inputStyle}
                value={patient.phone}
                onChange={(e) => setPatientField("phone", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Email
              <input
                style={inputStyle}
                value={patient.email}
                onChange={(e) => setPatientField("email", e.target.value)}
              />
            </label>
          </div>

          <div style={gridStyle}>
            <label style={labelStyle}>
              Street Address
              <input
                style={inputStyle}
                value={patient.streetAddress}
                onChange={(e) =>
                  setPatientField("streetAddress", e.target.value)
                }
              />
            </label>

            <label style={labelStyle}>
              City
              <input
                style={inputStyle}
                value={patient.city}
                onChange={(e) => setPatientField("city", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              State
              <input
                style={inputStyle}
                value={patient.state}
                onChange={(e) => setPatientField("state", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              ZIP
              <input
                style={inputStyle}
                value={patient.zip}
                onChange={(e) => setPatientField("zip", e.target.value)}
              />
            </label>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Encounter</h2>

          <div style={gridStyle}>
            <label style={labelStyle}>
              Date of Incident
              <input
                type="date"
                style={inputStyle}
                value={encounterForm.arrivalDate}
                onChange={(e) => setEncounterField("arrivalDate", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Time of Incident
              <input
                style={inputStyle}
                value={encounterForm.arrivalTime}
                onChange={(e) => setEncounterField("arrivalTime", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Report Completed By
              <input
                style={inputStyle}
                value={encounterForm.reportCompletedBy}
                onChange={(e) =>
                  setEncounterField("reportCompletedBy", e.target.value)
                }
              />
            </label>

            <label style={labelStyle}>
              Chief Complaint
              <input
                style={inputStyle}
                value={encounterForm.chiefComplaint}
                onChange={(e) =>
                  setEncounterField("chiefComplaint", e.target.value)
                }
              />
            </label>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Medical</h2>

          <div style={gridStyle}>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                First Aid Given?
              </div>
              <YesNoButtons
                value={medicalForm.firstAidGiven}
                disabled={generating}
                onChange={(value) => {
                  setMedicalField("firstAidGiven", value);
                  setIncidentForm((prev) => ({ ...prev, firstAidGiven: value }));
                }}
              />
            </div>

            <label style={labelStyle}>
              Body Part Injured
              <input
                style={inputStyle}
                value={medicalForm.bodyPartInjured}
                onChange={(e) =>
                  setMedicalField("bodyPartInjured", e.target.value)
                }
              />
            </label>

            <label style={labelStyle}>
              Disposition
              <select
                style={inputStyle}
                value={medicalForm.disposition}
                onChange={(e) => setMedicalField("disposition", e.target.value)}
              >
                <option value="">Select</option>
                <option value="dismissed">Dismissed</option>
                <option value="referredToPhysician">Referred to Physician</option>
                <option value="sentToHospital">Sent to Hospital</option>
                <option value="personalCar">Personal Car</option>
                <option value="rescueUnit">Rescue Unit</option>
                <option value="declinedTreatment">Declined Treatment</option>
              </select>
            </label>
          </div>

          <label style={labelStyle}>
            Treatment Rendered
            <textarea
              style={textareaStyle}
              value={medicalForm.planTreatmentDisposition}
              onChange={(e) =>
                setMedicalField("planTreatmentDisposition", e.target.value)
              }
            />
          </label>
        </div>

        <IncidentSection
          selectedEncounterId="standalone"
          incidentForm={incidentForm}
          setIncidentForm={setIncidentForm}
          medicalForm={medicalForm}
          setMedicalForm={setMedicalForm}
          encounterForm={encounterForm}
          setEncounterForm={setEncounterForm}
          expandedSections={{ incident: true }}
          toggleSection={() => {}}
          sectionRef={null}
          activeSection="incident"
        />

        <SignatureSection
          selectedEncounterId="standalone"
          signatureForm={signatureForm}
          setSignatureField={setSignatureField}
          expandedSections={{ signatures: true }}
          toggleSection={() => {}}
          sectionRef={null}
          activeSection="signatures"
          alwaysOpen
        />

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            style={{
              ...primaryButtonStyle,
              opacity: generating ? 0.8 : 1,
              cursor: generating ? "wait" : "pointer",
            }}
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "Generating..." : "Generate Incident Report"}
          </button>

          <button
            type="button"
            style={{
              ...secondaryButtonStyle,
              opacity: generating ? 0.6 : 1,
              cursor: generating ? "not-allowed" : "pointer",
            }}
            onClick={handleCancel}
            disabled={generating}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}